import React from "react"
import { Form, Input, Button, Checkbox } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { SocialButtons } from "../SocialButtons"
import { Title, Text } from "ui/Typography"
import { signIn, setTempToken } from "store/auth"
import { routes } from "router"
import useFormErrors from "hooks/useAntdFormErrors"
import { CustomInputError } from "ui/CustomInputError"
import { getRole, isProApp } from "utils/app-type"
import { getHostsByEnvironment } from "utils/host-urls"

export const SignIn = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading } = useSelector((state) => state.auth)
  const [form] = Form.useForm()
  const role = getRole()
  const { PROTOCOL, PRO_LANDING, CUSTOMER } = getHostsByEnvironment()
  const isPro = isProApp()
  const { state } = useLocation()

  const onFinish = (user) => {
    dispatch(signIn({ ...user, role }))
      .then(() => {
        if (role === "user") {
          navigate(routes.clientRootPath(), { replace: true })
        } else if (role === "guide_user" || role === "staff_user") {
          navigate(routes.proHomeRootPath(), { replace: true })
        }
      })
      .catch((err) => {
        const { email_verified, phone_number_verified, encrypted_uuid, phone_number } = err?.data || {}
        if (encrypted_uuid === undefined) return
        dispatch(setTempToken(encrypted_uuid))
        if (email_verified === false) {
          navigate(routes.confirmEmail({}, { email: user.email }), { state: { fromSignIn: true } })
        } else if (phone_number_verified === false) {
          navigate(routes.confirmPhone(), { state: { fromSignIn: true, phone_number } })
        }
      })
  }

  const { errors } = useFormErrors(form)
  return (
    <>
      <Title level={2} className="justify-center mb-3xl">
        {isPro ? t("sign_in.log_in_as_pro") : t("sign_in.log_in_as_guest")}
      </Title>
      <SocialButtons />
      <Form form={form} name="login" initialValues={{ remember: true, email: state?.email || "" }} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label={t("sign_in.email")}
          name="email"
          required={false}
          rules={[
            { required: true, message: t("form_validation.email_required") },
            { type: "email", message: t("form_validation.email") }
          ]}
          help={!!errors?.email ? <CustomInputError errorKey={errors.email} /> : null}
        >
          <Input
            placeholder={t("sign_in.email")}
            onChange={(e) => {
              const value = e.target.value.replace(/\s/g, "")
              form.setFieldsValue({ email: value })
            }}
          />
        </Form.Item>
        <Form.Item
          label={t("sign_in.password")}
          name="password"
          required={false}
          rules={[
            { required: true, message: t("form_validation.password_required") },
            { min: 8, message: t("form_validation.password_length") }
          ]}
          onChange={(e) => {
            const value = e.target.value.replace(/\s/g, "")
            form.setFieldsValue({ password: value })
          }}
          help={!!errors?.password ? <CustomInputError errorKey={errors?.password} /> : null}
        >
          <Input.Password placeholder={t("sign_in.password")} />
        </Form.Item>
        <Form.Item name="remember_me" valuePropName="checked">
          <div className="flex justify-between">
            <Checkbox value="remember_me">
              <Text type="secondary" variant="body">
                {t("sign_in.remember_me")}
              </Text>
            </Checkbox>
            <Text link>
              <Link to={routes.passwordRecovery()}>{t("sign_in.forgot_password")}?</Link>
            </Text>
          </div>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" block size="large" disabled={loading}>
            {t("sign_in.email_login")}
          </Button>
        </Form.Item>
      </Form>
      {!!errors?.base ? <CustomInputError errorKey={errors.base} /> : null}
      {!isPro && (
        <div className="flex-column gap-xs mt-xs">
          <div className="justify-center">
            <Text type="secondary" variant="body">
              {t("sign_in.dont_have_an_account")}
            </Text>
            <Text className="ml-xs" link>
              <Link to={routes.signUpOptions()}>{t("sign_up.register_guest_account")}</Link>
            </Text>
          </div>
          <div className="justify-center">
            <Text type="secondary" variant="body">
              {t("sign_in.is_pro_user")}
            </Text>
            <Text className="ml-xs" link>
              <Link to={PROTOCOL + PRO_LANDING} target="_blank">
                {t("sign_in.redirect_to_pro")}
              </Link>
            </Text>
          </div>
        </div>
      )}
      {isPro && (
        <div className="flex-column gap-xs mt-xs">
          <div className="flex justify-center">
            <Text type="secondary" variant="body">
              {t("sign_in.is_guest_user")}
            </Text>
            <Text className="ml-xs" link>
              <Link to={`${PROTOCOL}${CUSTOMER}`}>{t("sign_in.redirect_to_pro")}</Link>
            </Text>
          </div>
        </div>
      )}
    </>
  )
}
